/**
 * Pure reducers for self-schedule state.
 *
 * State is the three-facet `DayState` — see `./types.ts`. Every
 * reducer moves exactly one facet at a time:
 *
 *   | Reducer        | `customPicks` | `droppedIds` | `autoFillRequested` |
 *   | -------------- | ------------- | ------------ | ------------------- |
 *   | `clickCreate`  | append        | —            | —                   |
 *   | `toggleDay(t)` | —             | reset        | true                |
 *   | `toggleDay(f)` | reset         | reset        | false               |
 *   | `removeSlot`   | filter        | add (if gen) | auto-off if drained |
 *   | `prune`        | reset         | reset        | —                   |
 *   | `clearAll`     | reset-all     | reset-all    | false-all           |
 *   | `restretch`    | retime        | —            | —                   |
 *
 * Find Time is a fanned-out `toggleDay(true)` over N target days —
 * see `findTime/applyFindTime.ts`.
 */
import type { SelfSlot } from '../types';
import { DISCARD_FULLY_COVERED_CLICKS } from '../merge/constants';
import { fullyCoveredBy } from '../merge/fullyCoveredBy';
import { mergePicks } from '../merge/mergePicks';
import { toMs } from '../merge/toMs';
import { EMPTY_DAY, type DayState, type DayStateMap } from './types';

// ─────────────────────────────────────────────────────────────────
// Constants — tune here, not in the reducer bodies.
// ─────────────────────────────────────────────────────────────────

/**
 * Prefix for custom-slot ids. Generated ids come from the pipeline
 * (`gen-…`); custom ids come from reducers. Kept distinct so origin
 * is recoverable from the id alone.
 */
const CUSTOM_ID_PREFIX = 'custom';

function newCustomId(dateKey: string): string {
  return `${CUSTOM_ID_PREFIX}-${dateKey}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 6)}`;
}

// ─────────────────────────────────────────────────────────────────
// Small helpers.
// ─────────────────────────────────────────────────────────────────

function getDay(state: DayStateMap, dateKey: string): DayState {
  return state[dateKey] ?? EMPTY_DAY;
}

function setDay(
  state: DayStateMap,
  dateKey: string,
  next: DayState
): DayStateMap {
  return { ...state, [dateKey]: next };
}

// ─────────────────────────────────────────────────────────────────
// Reducers.
// ─────────────────────────────────────────────────────────────────

export interface ClickCreateArgs {
  dateKey: string;
  startIso: string;
  endIso: string;
  timezone: string;
  /** Pipeline output for this day — needed so we can dedup against
   *  a generated slot that's currently emitted via auto-fill. */
  generatedForDay: SelfSlot[];
}

/**
 * User single-click / drag-release. Appends a custom slot unless
 * the proposed window is fully covered by an active pick (effective
 * after merge). No-op → no duplicate.
 *
 * Does NOT flip `autoFillRequested`. See legacy rule 3.
 */
export function clickCreate(
  state: DayStateMap,
  { dateKey, startIso, endIso, timezone, generatedForDay }: ClickCreateArgs
): DayStateMap {
  const day = getDay(state, dateKey);

  if (DISCARD_FULLY_COVERED_CLICKS) {
    const effective = mergePicks({
      customPicks: day.customPicks,
      generated: generatedForDay,
      autoFillRequested: day.autoFillRequested,
      droppedIds: day.droppedIds,
    });
    const windowMs = {
      startMs: toMs(startIso, timezone),
      endMs: toMs(endIso, timezone),
    };
    if (fullyCoveredBy(windowMs, effective, timezone)) {
      // Click landed inside an existing pick — drop silently.
      return state;
    }
  }

  const newPick: SelfSlot = {
    id: newCustomId(dateKey),
    start: startIso,
    end: endIso,
    dateKey,
    origin: 'custom',
  };

  return setDay(state, dateKey, {
    ...day,
    customPicks: [...day.customPicks, newPick],
  });
}

export interface ToggleDayArgs {
  dateKey: string;
  checked: boolean;
}

/**
 * User clicked the day checkbox.
 *
 *   checked=true  → auto-fill on. Clear any prior `droppedIds` so
 *                  slots the user had previously removed are
 *                  repopulated. Customs are preserved.
 *
 *   checked=false → auto-fill off. Clear `droppedIds` + customs
 *                  (legacy parity — uncheck wipes the day).
 */
export function toggleDay(
  state: DayStateMap,
  { dateKey, checked }: ToggleDayArgs
): DayStateMap {
  const day = getDay(state, dateKey);
  if (checked) {
    return setDay(state, dateKey, {
      customPicks: day.customPicks,
      droppedIds: new Set(),
      autoFillRequested: true,
    });
  }
  return setDay(state, dateKey, {
    customPicks: [],
    droppedIds: new Set(),
    autoFillRequested: false,
  });
}

export interface RemoveSlotArgs {
  slotId: string;
  /**
   * Whether the slot id belonged to a generated slot. The calendar
   * knows from `origin` on the effective pick; passing it here
   * avoids the reducer guessing from the id prefix.
   */
  isGenerated: boolean;
}

/**
 * User clicked × on a slot pill.
 *
 *   custom    → drop from `customPicks`.
 *   generated → stash id in `droppedIds` so the merger stops
 *               emitting it. If that leaves no remaining generated
 *               slots (whole set dropped), clear `autoFillRequested`
 *               so re-clicking the day wipes `droppedIds` cleanly.
 */
export function removeSlot(
  state: DayStateMap,
  { slotId, isGenerated }: RemoveSlotArgs
): DayStateMap {
  if (isGenerated) {
    // Find the day via id prefix — generated ids are
    // `gen-${dateKey}-…`; seed ids are `seed-${dateKey}-…`.
    for (const [dateKey, day] of Object.entries(state)) {
      if (!slotId.includes(`-${dateKey}-`)) continue;
      const droppedIds = new Set(day.droppedIds);
      droppedIds.add(slotId);
      return setDay(state, dateKey, {
        ...day,
        droppedIds,
      });
    }
    // No day matched — id must have come from a day not yet in
    // state. Add a skeleton entry with the id dropped so the
    // merger honours it when generated arrives.
    // (Rare; defensive.)
    return state;
  }
  for (const [dateKey, day] of Object.entries(state)) {
    const idx = day.customPicks.findIndex((p) => p.id === slotId);
    if (idx < 0) continue;
    const nextCustoms = day.customPicks.filter((_, i) => i !== idx);
    return setDay(state, dateKey, { ...day, customPicks: nextCustoms });
  }
  return state;
}

export interface MoveSlotArgs {
  /** Id of the slot being dragged. */
  slotId: string;
  /** `true` if the slot originally came from the pipeline. */
  wasGenerated: boolean;
  /** New day the slot is being dropped on. */
  newDateKey: string;
  /** New position — ISO-8601 strings. */
  newStartIso: string;
  newEndIso: string;
}

/**
 * User drag-moved a picked slot to a new position.
 *
 * Custom → custom move: find the slot across days, update its
 * `dateKey / start / end` **in place (same id)** so FullCalendar
 * sees a property change, not an unmount+remount. Prevents the
 * "slot briefly reverts to original position" flicker on cross-day
 * drags.
 *
 * Generated → custom move: stash the old id in the source day's
 * `droppedIds` (so the merger stops emitting it) and materialise a
 * new custom with a fresh id at the destination. Generated ids
 * encode the start timestamp, so we can't reuse them after a move.
 *
 * This reducer never checks overlap — the caller does that check
 * against `activeSlots` before dispatching.
 */
export function moveSlot(
  state: DayStateMap,
  { slotId, wasGenerated, newDateKey, newStartIso, newEndIso }: MoveSlotArgs
): DayStateMap {
  if (wasGenerated) {
    // Find the slot's current day via the generated id prefix
    // (`gen-${dateKey}-…`) and stash it in that day's droppedIds.
    let next = state;
    for (const [dateKey, day] of Object.entries(state)) {
      if (!slotId.startsWith(`gen-${dateKey}-`)) continue;
      const droppedIds = new Set(day.droppedIds);
      droppedIds.add(slotId);
      next = setDay(next, dateKey, { ...day, droppedIds });
      break;
    }
    // Add a fresh custom at the new location.
    const destDay = getDay(next, newDateKey);
    return setDay(next, newDateKey, {
      ...destDay,
      customPicks: [
        ...destDay.customPicks,
        {
          id: newCustomId(newDateKey),
          start: newStartIso,
          end: newEndIso,
          dateKey: newDateKey,
          origin: 'custom',
        },
      ],
    });
  }

  // Custom → custom: update in place (same id, new position).
  for (const [oldDateKey, day] of Object.entries(state)) {
    const idx = day.customPicks.findIndex((p) => p.id === slotId);
    if (idx < 0) continue;
    const original = day.customPicks[idx];
    if (!original) return state;

    // User edit invalidates server-sourced override flags. The payload
    // mapper will recompute `is_custom` / `override_type` from the new
    // position. Strip everything; lifting individual fields would be
    // tempting but risky — partial strip leaves stale data.
    const moved: SelfSlot = {
      id: original.id,
      start: newStartIso,
      end: newEndIso,
      dateKey: oldDateKey === newDateKey ? oldDateKey : newDateKey,
      origin: original.origin,
      ...(original.isBooked ? { isBooked: true } : {}),
    };

    if (oldDateKey === newDateKey) {
      // Same-day move — just patch in place.
      const nextCustoms = [...day.customPicks];
      nextCustoms[idx] = moved;
      return setDay(state, oldDateKey, { ...day, customPicks: nextCustoms });
    }

    // Cross-day move — filter from old day, append to new day with
    // same id so FullCalendar treats it as a property update.
    const withoutSlot = day.customPicks.filter((_, i) => i !== idx);
    const withoutDayState = setDay(state, oldDateKey, {
      ...day,
      customPicks: withoutSlot,
    });
    const destDay = getDay(withoutDayState, newDateKey);
    return setDay(withoutDayState, newDateKey, {
      ...destDay,
      customPicks: [...destDay.customPicks, moved],
    });
  }
  return state;
}

/**
 * Roster change (interviewers / meeting rooms). Wipe customs +
 * dropped-ids; keep `autoFillRequested` so the fresh generated
 * slots repopulate automatically. Legacy rule 13 (clearCustomSlots).
 */
export function prune(state: DayStateMap): DayStateMap {
  let touched = false;
  const next: DayStateMap = {};
  for (const [dateKey, day] of Object.entries(state)) {
    if (day.customPicks.length === 0 && day.droppedIds.size === 0) {
      next[dateKey] = day;
      continue;
    }
    touched = true;
    next[dateKey] = {
      customPicks: [],
      droppedIds: new Set(),
      autoFillRequested: day.autoFillRequested,
    };
  }
  return touched ? next : state;
}

/** Wipe everything — Clear button, or roster-goes-empty. */
export function clearAll(): DayStateMap {
  return {};
}

export interface ReconcileSeedArgs {
  /** Fresh pipeline output for every day that has availability. */
  generatedByDay: Record<string, SelfSlot[]>;
}

/**
 * One-shot reconciliation after edit-mode seeding.
 *
 * On edit, `seedFromInitial` puts every backend `time_slot` into
 * `customPicks` with `autoFillRequested=false`. That renders the
 * checkbox as "partial" even when the user had selected ALL slots for
 * that day on save. Once the pipeline generates fresh slots for each
 * day, we diff customs against generated:
 *
 *   - customs ≡ generated (every generated id has a matching pick) →
 *     user had the full day. Drop customs, set `autoFillRequested=true`
 *     with empty `droppedIds` → checkbox flips to "full".
 *   - customs ⊂ generated (all picks match a generated id but some
 *     are missing) → user had auto-fill on and dropped a few. Move
 *     the missing ids into `droppedIds`, clear customs, auto-fill on.
 *     Checkbox is "partial" but the UI represents the true state.
 *   - customs have off-grid picks (not in generated) → leave as-is.
 *     Those are genuine custom clicks — user chose an odd time.
 */
export function reconcileSeed(
  state: DayStateMap,
  { generatedByDay }: ReconcileSeedArgs
): DayStateMap {
  const next: DayStateMap = {};
  let touched = false;
  // `restretchCustoms` normalises seeded ends to `new Date().toISOString()`
  // (UTC `Z` suffix), while pipeline slots can carry the IANA offset —
  // string equality on `${start}|${end}` would miss the match. Compare
  // on epoch ms instead so `2026-04-21T09:00:00+05:30` and the
  // equivalent `2026-04-21T03:30:00.000Z` collide correctly.
  const keyOfMs = (s: SelfSlot) =>
    `${Date.parse(s.start)}|${Date.parse(s.end)}`;
  for (const [dateKey, day] of Object.entries(state)) {
    const generated = generatedByDay[dateKey] ?? [];
    if (generated.length === 0 || day.customPicks.length === 0) {
      next[dateKey] = day;
      continue;
    }
    // Send-additional-slots (G2): the booked slot is seeded with
    // `isBooked: true` so the calendar renders it with the locked
    // treatment (× hidden, drag/resize disabled). Clearing customPicks
    // here would drop the flag — the slot would re-appear as a regular
    // pipeline-generated pick and the candidate's confirmed slot would
    // suddenly be removable. Skip reconcile for this day; the per-day
    // checkbox stays "partial", but the lock is preserved.
    const hasBookedPick = day.customPicks.some((c) => c.isBooked);
    if (hasBookedPick) {
      next[dateKey] = day;
      continue;
    }
    const generatedByMs = new Map<string, SelfSlot>();
    for (const g of generated) {
      generatedByMs.set(keyOfMs(g), g);
    }
    const matchedGeneratedIds = new Set<string>();
    let allOnGrid = true;
    for (const c of day.customPicks) {
      const match = generatedByMs.get(keyOfMs(c));
      if (match) {
        matchedGeneratedIds.add(match.id);
      } else {
        allOnGrid = false;
        break;
      }
    }
    if (!allOnGrid) {
      next[dateKey] = day;
      continue;
    }
    const droppedIds = new Set<string>();
    for (const g of generated) {
      if (!matchedGeneratedIds.has(g.id)) droppedIds.add(g.id);
    }
    next[dateKey] = {
      customPicks: [],
      droppedIds,
      autoFillRequested: true,
    };
    touched = true;
  }
  return touched ? next : state;
}

export interface ApplyFindTimeArgs {
  /** Dates the walker resolved for the user's Find Time option. */
  targetDateKeys: string[];
  /**
   * Default true. When true, prior picks (custom + generated drops)
   * across *every* day are wiped before the walker's targets are
   * marked auto-filled. Legacy `useSchFindTime`'s `clearBeforeFindTime`
   * default — replace, don't accumulate.
   */
  clearBeforeApply?: boolean;
}

/**
 * Apply a Find Time result. Clears the whole map (optional, default)
 * and sets `autoFillRequested=true` on each target date. Customs on
 * non-target days get wiped too — Find Time replaces, it doesn't
 * accumulate.
 */
export function applyFindTime(
  state: DayStateMap,
  { targetDateKeys, clearBeforeApply = true }: ApplyFindTimeArgs
): DayStateMap {
  const base = clearBeforeApply ? {} : { ...state };
  const next: DayStateMap = { ...base };
  for (const dateKey of targetDateKeys) {
    next[dateKey] = {
      customPicks: next[dateKey]?.customPicks ?? [],
      droppedIds: new Set(),
      autoFillRequested: true,
    };
  }
  return next;
}

export interface RestretchCustomsArgs {
  durationMinutes: number;
  timezone: string;
}

/**
 * Duration change restretches each custom's end-time so the payload
 * matches the form's `duration`. Generated slots come fresh from
 * the pipeline so they're naturally in sync — we only touch
 * `customPicks`.
 */
export function restretchCustoms(
  state: DayStateMap,
  { durationMinutes, timezone }: RestretchCustomsArgs
): DayStateMap {
  const stepMs = durationMinutes * 60_000;
  let touched = false;
  const next: DayStateMap = {};
  for (const [dateKey, day] of Object.entries(state)) {
    const nextCustoms = day.customPicks.map((p) => {
      const startMs = toMs(p.start, timezone);
      const newEndMs = startMs + stepMs;
      const endIso = new Date(newEndMs).toISOString();
      // Compare on epoch ms — BE may emit `+05:30` (or any IANA offset)
      // while `toISOString()` always yields `.000Z`. A string-equality
      // check treats those as different and wipes `_fromServer` on
      // every mount, breaking BE override_type round-trip (G7,
      // surfaced by SCH-34 verification logs).
      const oldEndMs = toMs(p.end, timezone);
      if (Number.isFinite(oldEndMs) && newEndMs === oldEndMs) return p;
      touched = true;
      // End-time changed — server-sourced override flags no longer
      // describe the slot's footprint. Strip them so the payload
      // mapper recomputes from the new end. Same rationale as
      // `moveSlot`'s strip — see comment there.
      return {
        id: p.id,
        start: p.start,
        end: endIso,
        dateKey: p.dateKey,
        origin: p.origin,
        ...(p.isBooked ? { isBooked: true } : {}),
      } satisfies SelfSlot;
    });
    next[dateKey] = { ...day, customPicks: nextCustoms };
  }
  return touched ? next : state;
}
